import React, { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Plus, Pencil, Trash2, X, Copy, Check } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

export default function AdminVouchers() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(null);
  const [copied, setCopied] = useState('');
  const { toast } = useToast();

  const load = () => api.admin.vouchers().then(setRows).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const payload = {
        code: form.code, discountPercent: Number(form.discountPercent),
        maxDiscount: Number(form.maxDiscount || 0), minSubtotal: Number(form.minSubtotal || 0),
        expiry: form.expiry || null, active: !!form.active,
      };
      if (form.id) await api.admin.voucherUpdate(form.id, payload);
      else await api.admin.voucherCreate(payload);
      toast({ title: form.id ? 'Voucher updated' : 'Voucher created' });
      setForm(null); load();
    } catch (e) { toast({ title: 'Failed', description: e.response?.data?.detail, variant: 'destructive' }); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this voucher?')) return;
    await api.admin.voucherDelete(id);
    load();
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopied(code);
    setTimeout(() => setCopied(''), 1500);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-extrabold">Vouchers</h1>
          <p className="text-gray-500 text-sm mt-1">{rows.length} coupon codes</p>
        </div>
        <Button onClick={() => setForm({ code: '', discountPercent: 10, maxDiscount: 0, minSubtotal: 0, expiry: '', active: true })} className="bg-[#6b47dc] hover:bg-[#5a3ac0]"><Plus size={16} className="mr-1" /> New voucher</Button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {rows.map(r => (
          <div key={r.id} className="relative overflow-hidden rounded-2xl border-2 border-dashed border-[#6b47dc]/30 bg-gradient-to-br from-purple-50 to-orange-50 p-5">
            <div className="absolute top-3 right-3 flex gap-1">
              <button onClick={() => setForm(r)} className="p-1.5 text-[#6b47dc] hover:bg-white rounded"><Pencil size={13} /></button>
              <button onClick={() => remove(r.id)} className="p-1.5 text-red-600 hover:bg-white rounded"><Trash2 size={13} /></button>
            </div>
            <div className="text-4xl font-extrabold gradient-text mb-2">{r.discountPercent}%</div>
            <div className="text-sm text-gray-600 mb-3">OFF discount</div>
            <button onClick={() => copyCode(r.code)} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white border-2 border-dashed border-[#6b47dc] font-mono font-bold text-[#6b47dc] text-sm hover:bg-purple-50">
              {r.code} {copied === r.code ? <Check size={14} /> : <Copy size={14} />}
            </button>
            <div className="mt-3 text-[11px] text-gray-500 space-y-0.5">
              {r.minSubtotal > 0 && <div>Min order: ${r.minSubtotal.toLocaleString()}</div>}
              {r.maxDiscount > 0 && <div>Max discount: ${r.maxDiscount.toLocaleString()}</div>}
              <div>Status: <span className={`font-bold ${r.active ? 'text-emerald-600' : 'text-gray-400'}`}>{r.active ? 'Active' : 'Inactive'}</span></div>
            </div>
          </div>
        ))}
      </div>

      {form && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setForm(null)}>
          <div className="bg-white rounded-2xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-gray-100 flex items-center justify-between">
              <h3 className="font-extrabold text-lg">{form.id ? 'Edit voucher' : 'New voucher'}</h3>
              <button onClick={() => setForm(null)}><X size={18} /></button>
            </div>
            <div className="p-5 space-y-3">
              <div><label className="text-xs font-bold text-gray-500">Code</label><input className="w-full px-3 py-2 rounded-lg border border-gray-200 outline-none focus:border-[#6b47dc] uppercase font-mono" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="WELCOME20" /></div>
              <div><label className="text-xs font-bold text-gray-500">Discount %</label><input type="number" className="w-full px-3 py-2 rounded-lg border border-gray-200 outline-none focus:border-[#6b47dc]" value={form.discountPercent} onChange={(e) => setForm({ ...form, discountPercent: e.target.value })} /></div>
              <div><label className="text-xs font-bold text-gray-500">Minimum subtotal</label><input type="number" className="w-full px-3 py-2 rounded-lg border border-gray-200 outline-none focus:border-[#6b47dc]" value={form.minSubtotal} onChange={(e) => setForm({ ...form, minSubtotal: e.target.value })} /></div>
              <div><label className="text-xs font-bold text-gray-500">Maximum discount (0 = no cap)</label><input type="number" className="w-full px-3 py-2 rounded-lg border border-gray-200 outline-none focus:border-[#6b47dc]" value={form.maxDiscount} onChange={(e) => setForm({ ...form, maxDiscount: e.target.value })} /></div>
              <label className="flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="accent-[#6b47dc]" /> Active</label>
            </div>
            <div className="p-5 border-t border-gray-100 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setForm(null)}>Cancel</Button>
              <Button onClick={save} className="bg-[#6b47dc] hover:bg-[#5a3ac0]">Save</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
